import React, { useEffect, useState, useContext } from 'react';

import cookie from 'react-cookies';
import { NotificationManager } from 'react-notifications';
import { useNavigate, useLocation } from 'react-router';

import jwtDecode from 'jwt-decode';

import { autorenewSession, noActionTimeout, showRenewModalTimeout } from '../config';

/** context */
import { UserContext } from '../context/UserContext';
import { cleanUpSessionData } from '../helpers/applicationHelp';
import { useRudder } from '../hooks/rudder';

/** service */
import { refreshToken } from '../service/api';
import { signOut } from '../service/profile';

const TokenRefresher = () => {
  const { token, setToken, setProfile, setShowSessionExtendModal, showSessionExtendModal } =
    useContext(UserContext);
  const { errorEvent } = useRudder();
  const navigate = useNavigate();
  const location = useLocation();

  /** token validity period which is reduced by 1 minute */
  const timeout = token ? jwtDecode(token).exp * 1000 - Date.now() - 60000 : 60000;

  const logout = async () => {
    try {
      await signOut().then(() =>
        NotificationManager.warning('Your session has expired', 'Warning', 10000)
      );
    } catch (e) {
      console.log('signout error', e);
    }
    cleanUpSessionData();
    if (!location.pathname.startsWith('/landing') || !location.pathname.startsWith('/auth')) {
      setProfile({});
      navigate(`/auth/sign-in?app=${location.pathname}`);
    }
  };

  useEffect(() => {
    if (timeout <= 0) {
      /** When the timeout value becomes minus value, don't call the refresh token api anymore, will be logged out soon */
      return;
    }
    const launchRefreshToken = () => {
      refreshToken()
        .then((res) => {
          cookie.save('lendistry_tokens_access', res.data.accessToken, {
            path: '/',
          });
          cookie.save('lendistry_tokens_refresh', res.data.refreshToken, {
            path: '/',
          });
          setToken(res.data.accessToken);
        })
        .catch((err) => {
          console.log(err);
          errorEvent('Error API', err);
          clearInterval(timer);
          logout();
        });
    };

    const checkTokenExpireTime = () => {
      const currentTime = new Date().getTime();
      const refreshTokenExpirationTime =
        jwtDecode(cookie.load('lendistry_tokens_access')).exp * 1000;
      return refreshTokenExpirationTime - currentTime;
    };

    const checkLastActionTime = () => {
      const currentTime = new Date().getTime();
      const lastActionTime = cookie.load('lendistry_last_interaction');
      return currentTime - lastActionTime;
    };

    const timer = setInterval(() => {
      const refreshKey = cookie.load('lendistry_tokens_refresh');
      if (!refreshKey) {
        if (
          !location.pathname.startsWith('/landing') &&
          !location.pathname.startsWith('/auth') &&
          !location.pathname.startsWith('/invite')
        ) {
          setProfile({});
          navigate('/landing');
        }
        return;
      }

      if (autorenewSession) {
        launchRefreshToken();
      } else {
        if (checkTokenExpireTime() <= showRenewModalTimeout) {
          if (checkLastActionTime() >= noActionTimeout) {
            if (!showSessionExtendModal) {
              setShowSessionExtendModal(true);
            }
          } else {
            launchRefreshToken();
          }
        } else {
        }
      }
    }, timeout);

    return () => {
      clearInterval(timer);
    };
    // eslint-disable-next-line
  }, [token, refreshToken]);

  return <React.Fragment />;
};

export default TokenRefresher;
